import React, { useState, useEffect, useCallback } from 'react';
import { Clock, User, Activity, Building, Calendar, Filter, Mail, MessageSquare } from 'lucide-react';
import { formatDateTime } from '../utils/dateUtils';
import { useSelector } from 'react-redux';
import type { RootState } from '../store/store';
import { AUDIT_ACTIONS } from '../utils/auditLogger';

interface AuditLogEntry {
  id: number;
  user_id: string;
  action: string;
  description: string;
  company: string;
  timestamp: string;
}

interface User {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string | null;
  isDisabled: boolean;
  isLockedOut: boolean;
  created_at: string;
  last_login: string | null;
  roles: string;
  hasBiometrics: boolean;
}

export default function AuditLogsPage() {
  const user = useSelector((state: RootState) => state.auth.user);
  const [auditLogs, setAuditLogs] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [selectedAction, setSelectedAction] = useState<string>('all');
  const [selectedUserId, setSelectedUserId] = useState<string>('all');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [limit, setLimit] = useState<number>(100);
  const [uniqueUsers, setUniqueUsers] = useState<string[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [allActions, setAllActions] = useState<string[]>([]);

  const fetchUsers = async () => {
    if (!user) return;
    
    try {
      setUsersLoading(true);
      
      const response = await fetch('https://s3.ts1cloud.com/service3/users', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'x-company-id': user.companyID?.toString() || '',
          'x-company-name': user.companyName || '',
        },
      });
      
      if (!response.ok) {
        throw new Error(response.status === 403 ? 'Permission denied' : `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setUsers(data);
      
    } catch (err) {
      console.error('Error fetching users:', err);
    } finally {
      setUsersLoading(false);
    }
  };

  const fetchAuditLogs = useCallback(async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Build query parameters
      const params = new URLSearchParams();
      if (selectedUserId !== 'all') {
        params.append('user_id', selectedUserId);
      }
      if (selectedAction !== 'all') {
        // DB stores snake_case (e.g. email_sent); exact match is case-sensitive in PostgreSQL
        params.append('action', selectedAction);
      }
      if (startDate && startDate.trim()) {
        const startDateTime = new Date(startDate + 'T00:00:00');
        if (!isNaN(startDateTime.getTime())) {
          params.append('startDate', startDateTime.toISOString());
        }
      }
      if (endDate && endDate.trim()) {
        const endDateTime = new Date(endDate + 'T23:59:59');
        if (!isNaN(endDateTime.getTime())) {
          params.append('endDate', endDateTime.toISOString());
        }
      }
      if (limit) {
        params.append('limit', limit.toString());
      }
      
      const url = `https://s3.ts1cloud.com/service3/audit-logs${params.toString() ? `?${params.toString()}` : ''}`;

      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'x-company-id': user.companyID?.toString() || '',
          'x-company-name': user.companyName || '',
        },
      });
      
      if (!response.ok) {
        throw new Error(response.status === 403 ? 'Permission denied' : `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setAuditLogs(data);
      
      // Extract unique users for filter dropdown
      const users = Array.from(new Set(data.map((log: AuditLogEntry) => log.user_id))).filter(Boolean) as string[];
      setUniqueUsers(users);
      
    } catch (err) {
      setError('Failed to load audit logs');
      console.error('Error fetching audit logs:', err);
    } finally {
      setLoading(false);
    }
  }, [user, selectedUserId, selectedAction, startDate, endDate, limit]);

  // Fetch all actions on initial load (without action filter)
  const fetchAllActions = async () => {
    if (!user) return;
    
    try {
      const response = await fetch(
        `https://s3.ts1cloud.com/service3/audit-logs?limit=1000`,
        {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'x-company-id': user.companyID?.toString() || '',
            'x-company-name': user.companyName || '',
          },
        }
      );
      
      if (!response.ok) {
        console.error('Failed to fetch all actions');
        return;
      }
      
      const data = await response.json();
      const actions = Array.from(new Set(data.map((log: AuditLogEntry) => log.action))).filter(Boolean) as string[];
      setAllActions(actions);
    } catch (err) {
      console.error('Error fetching all actions:', err);
    }
  };

  // Fetch users on component mount
  useEffect(() => {
    fetchUsers();
    fetchAllActions();
  }, [user]);

  // Fetch logs when filters change
  useEffect(() => {
    if (user) fetchAuditLogs();
  }, [user, fetchAuditLogs]);

  // Client-side filtering for description search only (other filters are server-side)
  const filteredLogs = auditLogs.filter(log => {
    const searchTerm = filter.toLowerCase();
    const description = (log.description || '').toLowerCase();
    const action = (log.action || '').toLowerCase();
    const userId = String(log.user_id || '');
    const company = (log.company || '').toLowerCase();
    
    return description.includes(searchTerm) ||
           action.includes(searchTerm) ||
           userId.includes(filter) ||
           company.includes(searchTerm);
  });

  // Build dropdown options: server-discovered actions merged with well-known ones so common filters
  // (e.g. Email Sent, SMS Sent) are always offered even before the first such event has been logged.
  const discoveredActions = allActions.length > 0 ? allActions : Array.from(new Set(auditLogs.map(log => log.action)));
  const uniqueActions = Array.from(new Set([
    ...discoveredActions,
    ...Object.values(AUDIT_ACTIONS),
  ])).filter(Boolean).sort();

  const handleRefresh = () => {
    fetchAuditLogs();
  };

  const handleClearFilters = () => {
    setFilter('');
    setSelectedAction('all');
    setSelectedUserId('all');
    setStartDate('');
    setEndDate('');
    setLimit(100);
    // Force a refresh after clearing filters
    setTimeout(() => {
      if (user) {
        fetchAuditLogs();
      }
    }, 100);
  };

  const getUserName = (userId: string) => {
    let user = users.find(u => u.id.toString() === userId)
      || users.find(u => u.id === parseInt(userId))
      || users.find(u => u.id.toString() === userId.toString());

    if (user) {
      const name = [user.firstName, user.lastName].filter(Boolean).join(' ');
      return name || (user.email ? user.email.split('@')[0] : userId);
    }

    return userId;
  };

  const getActionIconColor = (action: string) => {
    switch (action) {
      case 'user_login':   return 'text-green-600 dark:text-green-400';
      case 'user_logout':  return 'text-blue-600 dark:text-blue-400';
      case 'sensor_calibrate':
      case 'sensor_edit':
      case 'sensor_delete': return 'text-yellow-600 dark:text-yellow-400';
      case 'email_sent':   return 'text-purple-600 dark:text-purple-400';
      case 'sms_sent':     return 'text-pink-600 dark:text-pink-400';
      default:             return 'text-gray-500 dark:text-gray-400';
    }
  };

  const getActionIcon = (action: string) => {
    const cls = `h-4 w-4 ${getActionIconColor(action)}`;
    switch (action) {
      case 'user_login':
      case 'user_logout':
        return <User className={cls} />;
      case 'email_sent':
        return <Mail className={cls} />;
      case 'sms_sent':
        return <MessageSquare className={cls} />;
      case 'sensor_calibrate':
      case 'sensor_edit':
      case 'sensor_delete':
        return <Activity className={cls} />;
      default:
        return <Activity className={cls} />;
    }
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case 'user_login':
        return 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/20';
      case 'user_logout':
        return 'text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/20';
      case 'sensor_calibrate':
      case 'sensor_edit':
      case 'sensor_delete':
        return 'text-yellow-600 dark:text-yellow-400 bg-yellow-100 dark:bg-yellow-900/20';
      case 'email_sent':
        return 'text-purple-600 dark:text-purple-400 bg-purple-100 dark:bg-purple-900/20';
      case 'sms_sent':
        return 'text-pink-600 dark:text-pink-400 bg-pink-100 dark:bg-pink-900/20';
      default:
        return 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-900/20';
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Loading audit logs...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 p-4 rounded-md">
          <div className="text-red-700">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Audit Logs</h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Track user actions and system events
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 space-y-4">
        {/* Search and Action Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search descriptions..."
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md bg-app-inset dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>
          <div className="w-full sm:w-48">
            <select
              value={selectedAction}
              onChange={(e) => setSelectedAction(e.target.value)}
              className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md bg-app-inset dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="all">All Actions</option>
              {uniqueActions.map(action => (
                <option key={action} value={action}>
                  {action.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </option>
              ))}
            </select>
          </div>
          <div className="w-full sm:w-48">
            <select
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md bg-app-inset dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="all">All Users</option>
              {uniqueUsers.map(userId => {
                const userName = getUserName(userId);
                return (
                  <option key={userId} value={userId}>
                    {userName}
                  </option>
                );
              })}
            </select>
          </div>
          <div className="w-full sm:w-32">
            <select
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md bg-app-inset dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value={50}>50 logs</option>
              <option value={100}>100 logs</option>
              <option value={200}>200 logs</option>
              <option value={500}>500 logs</option>
            </select>
          </div>
        </div>

        {/* Date Range Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 sm:w-48">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md bg-app-inset dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
            {startDate && <div className="text-xs text-gray-500 mt-1">Selected: {startDate}</div>}
          </div>
          <div className="flex-1 sm:w-48">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md bg-app-inset dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
            {endDate && <div className="text-xs text-gray-500 mt-1">Selected: {endDate}</div>}
          </div>
          <div className="flex items-end gap-2">
            <button
              onClick={handleRefresh}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              Refresh
            </button>
            <button
              onClick={handleClearFilters}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Audit Logs Table */}
      <div className="bg-app-panel dark:bg-gray-800 shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Timestamp
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Action
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Description
                </th>
              </tr>
            </thead>
            <tbody className="bg-app-panel dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredLogs.map((log) => (
                <tr key={log.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                    <div className="flex items-center">
                      <Clock className="h-4 w-4 mr-2 text-gray-400" />
                      {new Date(log.timestamp).toLocaleString()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                    <div className="flex items-center">
                      <User className="h-4 w-4 mr-2 text-gray-400" />
                      {usersLoading ? 'Loading...' : getUserName(log.user_id)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {getActionIcon(log.action)}
                      <span className={`ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getActionColor(log.action)}`}>
                        {log.action.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </span>
                    </div>
                  </td>

                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                    <div className="max-w-md truncate" title={log.description}>
                      {log.description}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {filteredLogs.length === 0 && (
          <div className="text-center py-8">
            <div className="text-gray-500 dark:text-gray-400">
              {filter || selectedAction !== 'all' ? 'No audit logs match your filters' : 'No audit logs found'}
            </div>
          </div>
        )}
      </div>
    </div>
  );
} 